import type { RouteDef, RouteContext } from '../router';
import { ssrBodyFor } from '../router';
import { t, tData, withLocale, getLocale } from '../i18n';
import { renderGuideDevicePage, renderGuideHubPage, guidePlayer, type GuidesContent } from '@roamzy/shared';
import { trialCreditMicro } from '../trial-cta';

/**
 * Setup guides: /guides, /guides/ios, /guides/android.
 *
 * The markup is `@roamzy/shared` guide-manual.ts, the same functions the
 * server renders (backend util/guides-body.ts). Until 2026-09-30 this file
 * drew its own page, a one-step-at-a-time stepper with emoji phone mock-ups,
 * so an in-app navigation and a visit from search saw two different pages
 * behind one URL. The client now keeps the server's HTML on first paint and
 * renders the same markup on SPA navigation; all it adds is behaviour: the
 * step-by-step player (shared guidePlayer, which the server page also runs
 * inline) and the PWA install prompt.
 *
 * Each URL is its own RouteDef (2026-07-14): three spread copies of one
 * definition once rendered three identical pages, and GSC reported them as
 * duplicates. Keep in sync with backend util/meta.ts STATIC_META.
 */
type GuidePlatform = 'hub' | 'ios' | 'android';

function renderGuides(platform: GuidePlatform): string {
  const content = tData<GuidesContent>('guides');
  if (!content) return '';
  // The drawn dashboard card shows the free eSIM's real balance.
  const opts = { href: (p: string) => withLocale(p), home: t('nav.home'), trialCreditMicro: trialCreditMicro(), locale: getLocale() };
  return platform === 'hub'
    ? renderGuideHubPage(content, opts)
    : renderGuideDevicePage(content, platform, opts);
}

function makeGuides(platform: GuidePlatform): RouteDef {
  const path = platform === 'hub' ? '/guides' : `/guides/${platform}`;
  return {
    path,
    routeType: 'guides',
    navKey: 'guides',
    // Static EN fallback (RouteDef.metaTitle is a plain string and routes are
    // built at module load, before the locale is known); the server's
    // localized meta replaces it after a navigation (seo-overrides.ts).
    metaTitle: `${platform === 'ios' ? 'Install Roamzy on iPhone — Step by Step'
      : platform === 'android' ? 'Install Roamzy on Android — Step by Step'
      : 'Setup Guides — Install Roamzy on iPhone & Android'} | Roamzy`,
    metaDesc: 'Step-by-step eSIM setup guides for iPhone and Android. Install your eSIM in under two minutes.',
    // Keep the server-rendered body on first paint; it is the same markup
    // render() would produce.
    prerenderMarker: '.guides-route',
    hydrate: () => '',
    render() {
      return ssrBodyFor('.guides-route') ?? renderGuides(platform);
    },
    mount(root, ctx: RouteContext) {
      void ctx;
      // /guides#pwa (from the dashboard banner): the browser's own hash
      // scroll fires before the SPA has mounted the route, so redo it.
      if (window.location.hash) {
        const target = root.querySelector<HTMLElement>(window.location.hash);
        if (target) requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }

      // The server page ran it inline already; an in-app navigation did not
      // (scripts in innerHTML never run). It skips a player that is live.
      const player = root.querySelector<HTMLElement>('.gp');
      guidePlayer(player);
      // A link to a step (support sends them): the router has just scrolled
      // to the top, so bring the player into view on the next frame.
      if (player && new URLSearchParams(window.location.search).has('step')) {
        requestAnimationFrame(() => player.scrollIntoView({ block: 'start' }));
      }

      // PWA install via beforeinstallprompt (Android Chrome / Edge / Brave).
      // The event fires once per page load: keep it, reveal the button, fire
      // prompt() on click. iOS Safari has no such API; the listed Share-sheet
      // steps are the path there.
      type BeforeInstallPromptEvent = Event & {
        prompt: () => Promise<void>;
        userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
      };
      const installBtn = root.querySelector<HTMLButtonElement>('[data-pwa-install]');
      let deferredPrompt: BeforeInstallPromptEvent | null = null;
      const onBeforeInstallPrompt = (e: Event): void => {
        e.preventDefault();
        deferredPrompt = e as BeforeInstallPromptEvent;
        installBtn?.classList.add('pwa-install-btn--ready');
      };
      const onInstallClick = async (): Promise<void> => {
        if (!deferredPrompt) return;
        await deferredPrompt.prompt();
        try { await deferredPrompt.userChoice; } catch { /* ignore */ }
        deferredPrompt = null;
        installBtn?.classList.remove('pwa-install-btn--ready');
      };
      window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      installBtn?.addEventListener('click', onInstallClick);

      return () => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        installBtn?.removeEventListener('click', onInstallClick);
      };
    },
  };
}

export const guidesHub = makeGuides('hub');
export const guidesIos = makeGuides('ios');
export const guidesAndroid = makeGuides('android');

export default guidesHub;
